import React, { Component } from 'react';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import { PDFDocument } from 'pdf-lib';
import { showNotification } from '@mantine/notifications';
import { Group, Loader, Radio, RadioGroup, Select, Stack } from '@mantine/core';
import CustomModal from './index';
import ColorButton from '../Buttons/ColorButton';
import Autoload from '../../../../Autoload';
import InvoicePdfTemplate from '../../../../../views/pages/Module/General/profile/InvoiceTemplatePdf.jsx';
import GreenTick from '../../../../../assets/images/module/general/GreenTick.svg';
import RedCross from '../../../../../assets/images/module/general/RedCross.svg';
import Up from '../../../../../assets/images/module/general/SelectArrowUp.svg';
import Down from '../../../../../assets/images/module/general/SelectArrowDown.svg';

export class AssignInvoicesToReceiptModal extends Component {
  state = {
    selectedPropertyId: null,
    assignmentType: this.props.invoices.length === 1 ? 'one_receipt' : null,
    processing: false,
    processingIndex: 0,
    generatedFiles: [],
    btnLoader: false,
    propertyDropdownIcon: Down,
  };

  getInvoiceName = (invoice) => (invoice.positions && invoice.positions.length > 0 && invoice.positions[0].description) || `Rechnung ${invoice.invoiceNumber}`;

  truncateReceiptName = (name, maxLength = 48) => (name && name.length > maxLength ? `${name.slice(0, maxLength - 1).trimEnd()}…` : name);

  calculateVat = (amount, vatPercent = 19) => {
    const netAmount = amount / (1 + vatPercent / 100);
    const vatAmount = amount - netAmount;
    return {
      vatPercent,
      vatAmount: Math.round(vatAmount * 100) / 100,
      netAmount: Math.round(netAmount * 100) / 100,
    };
  };

  startProcessing = () => {
    this.setState({ btnLoader: true, processing: true, generatedFiles: [], processingIndex: 0 });
  };

  handlePdfGenerated = (file) => {
    const { invoices } = this.props;
    const { processingIndex, generatedFiles } = this.state;
    const invoice = invoices[processingIndex];
    const nextGeneratedFiles = [...generatedFiles, { invoice, file }];
    const nextIndex = processingIndex + 1;

    if (nextIndex >= invoices.length) {
      this.setState({ generatedFiles: nextGeneratedFiles, processingIndex: nextIndex }, () => this.finishProcessing());
    } else {
      this.setState({ generatedFiles: nextGeneratedFiles, processingIndex: nextIndex });
    }
  };

  mergeGeneratedFiles = async (files) => {
    const mergedPdf = await PDFDocument.create();
    for (const file of files) {
      const arrayBuffer = await file.arrayBuffer();
      const pdf = await PDFDocument.load(arrayBuffer);
      const pages = await mergedPdf.copyPages(pdf, pdf.getPageIndices());
      pages.forEach((page) => mergedPdf.addPage(page));
    }
    const mergedBytes = await mergedPdf.save();
    return new File([mergedBytes], `Rechnungen-zusammengefuehrt-${new Date().valueOf()}.pdf`, { type: 'application/pdf' });
  };

  createReceiptRequest = (payload) => {
    return new Promise((resolve, reject) => {
      this._createResolve = resolve;
      this._createReject = reject;
      const { vatPercent, vatAmount, netAmount } = this.calculateVat(payload.amount);
      const data = JSON.stringify({
        id: null,
        name: this.truncateReceiptName(payload.name),
        receiptDate: `${dayjs(payload.date).format('YYYY-MM-DD')}T00:00:00`,
        date: `${dayjs(payload.date).format('YYYY-MM-DD')}T00:00:00`,
        costs: payload.amount,
        cost: payload.amount,
        utilityBillingRelevant: false,
        receiptPeriodStart: `${dayjs(payload.date).format('YYYY-MM-DD')}T00:00:00`,
        receiptPeriodEnd: `${dayjs(payload.date).format('YYYY-MM-DD')}T00:00:00`,
        propertyId: payload.propertyId,
        selectedCostTypes: [],
        vatPercent,
        vatAmount,
        netAmount,
        taxCostType: 'Verwaltungskosten',
        receiptNumber: payload.receiptNumber,
        notes: null,
      });
      Autoload.HttpRequestAxios.HttpRequest(
        Autoload.Setting.rm_api_base_url + 'create',
        'POST',
        data,
        null,
        false,
        true,
        false,
        this,
        'handleCreateReceiptResponse',
        null,
        false,
        '',
        { 'Content-Type': 'application/json', Authorization: Autoload.Utils.getAuthToken() }
      );
    });
  };

  handleCreateReceiptResponse = (response) => {
    if (response.status === true && response.result) {
      this._createResolve(response.result);
    } else {
      this._createReject(new Error('create receipt failed'));
    }
  };

  uploadReceiptFileRequest = (receiptId, file) => {
    return new Promise((resolve, reject) => {
      this._uploadResolve = resolve;
      this._uploadReject = reject;
      const data = new FormData();
      data.append('files', file);
      Autoload.HttpRequestAxios.HttpRequest(
        Autoload.Setting.rm_api_base_url + 'upload/' + receiptId,
        'PUT',
        data,
        null,
        false,
        true,
        false,
        this,
        'handleUploadReceiptFileResponse',
        null,
        false,
        '',
        { 'Content-Type': 'multipart/form-data', Authorization: Autoload.Utils.getAuthToken() }
      );
    });
  };

  handleUploadReceiptFileResponse = (response) => {
    if (response.status === true) {
      this._uploadResolve(response);
    } else {
      this._uploadReject(new Error('upload receipt file failed'));
    }
  };

  createReceipt = async (payload) => {
    const receiptId = await this.createReceiptRequest(payload);
    await this.uploadReceiptFileRequest(receiptId, payload.file);
  };

  finishProcessing = async () => {
    const { assignmentType, selectedPropertyId, generatedFiles } = this.state;
    try {
      if (assignmentType === 'one_receipt') {
        const mergedFile = await this.mergeGeneratedFiles(generatedFiles.map((g) => g.file));
        const totalAmount = generatedFiles.reduce((sum, g) => sum + Math.abs(g.invoice.totalAmount), 0);
        const name = generatedFiles.length === 1
          ? this.getInvoiceName(generatedFiles[0].invoice)
          : 'hellohousing Zugang';
        const latestDate = new Date(Math.max(...generatedFiles.map((g) => new Date(g.invoice.invoiceDate).getTime())));
        await this.createReceipt({
          propertyId: selectedPropertyId,
          name,
          amount: totalAmount,
          date: latestDate,
          receiptNumber: null,
          file: mergedFile,
        });
      } else {
        for (const { invoice, file } of generatedFiles) {
          await this.createReceipt({
            propertyId: selectedPropertyId,
            name: this.getInvoiceName(invoice),
            amount: Math.abs(invoice.totalAmount),
            date: new Date(invoice.invoiceDate),
            receiptNumber: invoice.invoiceNumber,
            file,
          });
        }
      }
      showNotification({
        message: `Beleg${assignmentType === 'separate_receipts' && generatedFiles.length > 1 ? 'e' : ''} erfolgreich erstellt.`,
        icon: <img src={GreenTick} alt="green-tick" />,
      });
      this.setState({ btnLoader: false, processing: false }, () => this.props.toggle(true));
    } catch (e) {
      console.log('finishProcessing error: ', e);
      showNotification({
        message: 'Es ist ein Fehler aufgetreten. Bitte probieren Sie es erneut oder wenden Sie sich an den Support.',
        icon: <img src={RedCross} alt="red-cross" />,
      });
      this.setState({ btnLoader: false, processing: false });
    }
  };

  render() {
    const { open, toggle, className, invoices, properties } = this.props;
    const { selectedPropertyId, assignmentType, btnLoader, processing, processingIndex, propertyDropdownIcon } = this.state;

    return (
      <CustomModal
        heading={'Ausgewählte Rechnungen als Beleg speichern'}
        open={open}
        toggle={() => {
          if (!btnLoader) toggle();
        }}
        className={className}
      >
        <Stack gap={20}>
          {!processing && (
            <>
              <Stack gap={6}>
                <span className="modal-sub-headings" style={{ marginBottom: 10 }}>Immobilie</span>
                <Select
                  data={(properties || []).map((property) => ({ value: property.id, label: property.name }))}
                  value={selectedPropertyId}
                  onChange={(value) => this.setState({ selectedPropertyId: value })}
                  placeholder="Immobilie auswählen"
                  searchable
                  disabled={btnLoader}
                  rightSection={<img src={propertyDropdownIcon} alt="arrow" />}
                  onDropdownOpen={() => this.setState({ propertyDropdownIcon: Up })}
                  onDropdownClose={() => this.setState({ propertyDropdownIcon: Down })}
                  comboboxProps={{ withinPortal: false, zIndex: 9999999 }}
                />
              </Stack>

              {invoices.length > 1 && (
                <Stack gap={6} style={{ marginTop: 10 }}>
                  <span className="modal-sub-headings" style={{ marginBottom: 10 }}>Belegerstellung</span>
                  <RadioGroup value={assignmentType} onChange={(value) => this.setState({ assignmentType: value })}>
                    <Radio disabled={btnLoader} value="one_receipt" label={'Alle ausgewählten Rechnungen in einen Beleg zusammenführen.'} />
                    <Radio disabled={btnLoader} value="separate_receipts" label={'Für jede Rechnung einen eigenen Beleg erzeugen.'} />
                  </RadioGroup>
                </Stack>
              )}
            </>
          )}

          {processing && (
            <Group justify="center" align="flex-end" gap={3} style={{ padding: '20px 0' }}>
              <span style={{fontWeight: '400'}}>{`PDF wird erstellt (${Math.min(processingIndex + 1, invoices.length)}/${invoices.length})`}</span>
              <Loader size={16} type="dots" color="black" />
            </Group>
          )}

          <Group justify='space-between' style={{ marginTop: 10, width: '100%' }}>
            <div>
              <ColorButton text={'Abbrechen'} className="gray-btn" disabled={btnLoader} onClick={() => toggle()} />
            </div>
            <div>
              <ColorButton
                text={'Speichern'}
                className="purple-btn"
                showLoader={btnLoader}
                disabled={!selectedPropertyId || !assignmentType || btnLoader}
                onClick={() => this.startProcessing()}
              />
            </div>
          </Group>
        </Stack>

        {processing && processingIndex < invoices.length && (
          <div style={{ position: 'absolute', top: -99999, left: -99999, visibility: 'hidden' }}>
            <InvoicePdfTemplate
              key={invoices[processingIndex].id}
              id={invoices[processingIndex].id}
              background
              onPdfGenerated={this.handlePdfGenerated}
            />
          </div>
        )}
      </CustomModal>
    );
  }
}

const mapStateToProps = (state) => {
  return {
    properties: state.generalReducer.allProperties,
  };
};

export default connect(mapStateToProps)(AssignInvoicesToReceiptModal);
